import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Edit, Trash2, Files, Users, Mail, Phone, Calendar, Linkedin } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Initiative {
  id: string;
  name: string;
  description: string | null;
  deployment_date: string | null;
  created_at: string;
  updated_at: string;
  files?: InitiativeFile[];
  stats?: {
    clientCount: number;
    clients: Array<{ id: string; name: string }>;
    responseStatuses: {
      no_response: number;
      not_interested: number;
      interested: number;
      proposal: number;
    };
    clientsByStatus: {
      no_response: Array<{ id: string; name: string }>;
      not_interested: Array<{ id: string; name: string }>;
      interested: Array<{ id: string; name: string }>;
      proposal: Array<{ id: string; name: string }>;
    };
    activityTypes: {
      email: number;
      call: number;
      meeting: number;
      linkedin: number;
      other: number;
    };
  };
}

interface InitiativeFile {
  id: string;
  name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

interface InitiativeListProps {
  initiatives: Initiative[];
  isLoading?: boolean;
  onEdit: (initiative: Initiative) => void;
  onDelete: (initiative: Initiative) => void;
  onViewFiles?: (initiative: Initiative) => void;
}

export function InitiativeList({ initiatives, isLoading, onEdit, onDelete, onViewFiles }: InitiativeListProps) {
  if (initiatives.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No initiatives found</h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or create a new initiative.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableBody>
              {initiatives.map((initiative) => (
                <TableRow key={initiative.id}>
                  <TableCell colSpan={4} className="py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        {/* Name */}
                        <div className="text-base md:text-lg font-semibold leading-snug text-foreground">
                          {initiative.name}
                        </div>
                        
                        {/* Statistics */}
                        <div className="flex flex-wrap items-center gap-2">
                         {initiative.stats && (
                            <>
                              {/* Activity Summary Header */}
                              <div className="flex items-center gap-1 text-xs font-medium text-foreground">
                                <Users className="h-3 w-3" />
                                <span>Activity Summary:</span>
                              </div>
                              
                              {/* Client Count with Tooltip */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-block cursor-help">
                                    <Badge variant="outline" className="text-xs">
                                      {initiative.stats.clientCount > 0 
                                        ? `${initiative.stats.clientCount} client${initiative.stats.clientCount !== 1 ? 's' : ''}`
                                        : 'No activities'
                                      }
                                    </Badge>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {initiative.stats.clients && initiative.stats.clients.length > 0 ? (
                                    <div className="space-y-1">
                                      <div className="font-medium">Contacted clients:</div>
                                      {initiative.stats.clients.map((client) => (
                                        <div key={client.id} className="text-sm">
                                          • {client.name}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div>No clients contacted yet</div>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                              
                              {/* Response Status Badges - only show if there are activities */}
                              {initiative.stats.clientCount > 0 && (
                                <>
                                  {initiative.stats.responseStatuses.no_response > 0 && (
                                    <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                                      No response: {initiative.stats.responseStatuses.no_response}
                                    </Badge>
                                  )}
                                  {initiative.stats.responseStatuses.not_interested > 0 && (
                                    <Badge variant="secondary" className="text-xs bg-destructive/10 text-destructive">
                                      Not interested: {initiative.stats.responseStatuses.not_interested}
                                    </Badge>
                                  )}
                                   {initiative.stats.responseStatuses.interested > 0 && (
                                     <Tooltip>
                                       <TooltipTrigger asChild>
                                         <div className="inline-block cursor-help">
                                           <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                                             Interested: {initiative.stats.responseStatuses.interested}
                                           </Badge>
                                         </div>
                                       </TooltipTrigger>
                                       <TooltipContent>
                                         <div className="space-y-1">
                                           <div className="font-medium">Interested clients:</div>
                                           {initiative.stats.clientsByStatus?.interested?.map((client) => (
                                             <div key={client.id} className="text-sm">
                                               • {client.name}
                                             </div>
                                           ))}
                                         </div>
                                       </TooltipContent>
                                     </Tooltip>
                                   )}
                                   {initiative.stats.responseStatuses.proposal > 0 && (
                                     <Tooltip>
                                       <TooltipTrigger asChild>
                                         <div className="inline-block cursor-help">
                                           <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                                             Proposal: {initiative.stats.responseStatuses.proposal}
                                           </Badge>
                                         </div>
                                       </TooltipTrigger>
                                       <TooltipContent>
                                         <div className="space-y-1">
                                           <div className="font-medium">Clients with proposals:</div>
                                           {initiative.stats.clientsByStatus?.proposal?.map((client) => (
                                             <div key={client.id} className="text-sm">
                                               • {client.name}
                                             </div>
                                           ))}
                                         </div>
                                       </TooltipContent>
                                     </Tooltip>
                                    )}
                                 </>
                               )}
                               
                               {/* Activity Types - only show if there are activities */}
                               {initiative.stats.clientCount > 0 && initiative.stats.activityTypes && (
                                 <>
                                   <div className="w-full"></div> {/* Line break */}
                                   <div className="flex items-center gap-1 text-xs font-medium text-foreground">
                                     <span>Activity Types:</span>
                                   </div>
                                   
                                   {initiative.stats.activityTypes.email > 0 && (
                                     <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                       <Mail className="h-3 w-3 mr-1" />
                                       Emails: {initiative.stats.activityTypes.email}
                                     </Badge>
                                   )}
                                   {initiative.stats.activityTypes.call > 0 && (
                                     <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                                       <Phone className="h-3 w-3 mr-1" />
                                       Calls: {initiative.stats.activityTypes.call}
                                     </Badge>
                                   )}
                                   {initiative.stats.activityTypes.meeting > 0 && (
                                     <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                                       <Calendar className="h-3 w-3 mr-1" />
                                       Meetings: {initiative.stats.activityTypes.meeting}
                                     </Badge>
                                   )}
                                   {initiative.stats.activityTypes.linkedin > 0 && (
                                     <Badge variant="outline" className="text-xs bg-sky-50 text-sky-600 border-sky-200">
                                       <Linkedin className="h-3 w-3 mr-1" />
                                       LinkedIn: {initiative.stats.activityTypes.linkedin}
                                     </Badge>
                                   )}
                                   {initiative.stats.activityTypes.other > 0 && (
                                     <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200">
                                       Other: {initiative.stats.activityTypes.other}
                                     </Badge>
                                   )}
                                 </>
                               )}
                             </>
                           )}
                         </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
        {/* Date */}
        <div className="text-xs font-bold text-foreground">
          {(() => {
            try {
              // Use deployment_date if available, otherwise fall back to created_at
              const dateToDisplay = initiative.deployment_date || initiative.created_at;
              
              // Debug logging
              console.log('Date debug for', initiative.name, {
                deployment_date: initiative.deployment_date,
                created_at: initiative.created_at,
                dateToDisplay
              });
              
              // Check if we have a valid date value before processing
              if (!dateToDisplay || dateToDisplay === null || dateToDisplay === undefined || dateToDisplay === 'undefined') {
                console.log('No valid date found for', initiative.name);
                return 'No date set';
              }
              
              const date = new Date(dateToDisplay);
              if (isNaN(date.getTime())) {
                console.error('Invalid date found:', { 
                  deployment_date: initiative.deployment_date, 
                  created_at: initiative.created_at,
                  dateToDisplay: dateToDisplay,
                  initiative_name: initiative.name
                });
                return 'Invalid date format';
              }
              
              // Return just the formatted date without prefix
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            } catch (error) {
              console.error('Date parsing error:', error, {
                deployment_date: initiative.deployment_date,
                created_at: initiative.created_at,
                initiative_name: initiative.name
              });
              return 'Date error';
            }
          })()}
        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="Files" onClick={() => onViewFiles?.(initiative)}>
                                <Files className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Files</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => onEdit(initiative)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => onDelete(initiative)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}